import { CONNECTION_CATEGORIES, CATEGORY_BY_ID } from './categories';
import { CONNECTORS } from './connectors';
import type {
  ConnectionCategoryId,
  ConnectorCapability,
  ConnectorDefinition,
  ConnectorStatus,
} from './types';

export type {
  ConnectionCategoryId,
  ConnectionCategoryMeta,
  ConnectorCapability,
  ConnectorDefinition,
  ConnectorStatus,
  DataStoreConnection,
} from './types';

export { CONNECTION_CATEGORIES, CATEGORY_BY_ID, CONNECTORS };

/** Build lookup: lowercased alias/key → definition */
const buildLookup = (): Map<string, ConnectorDefinition> => {
  const map = new Map<string, ConnectorDefinition>();
  for (const connector of CONNECTORS) {
    map.set(connector.connectorKey.toLowerCase(), connector);
    if (connector.templateId) {
      map.set(connector.templateId.toLowerCase(), connector);
    }
    for (const alias of connector.aliases || []) {
      map.set(alias.toLowerCase(), connector);
    }
    map.set(connector.displayName.toLowerCase(), connector);
  }
  return map;
};

const LOOKUP = buildLookup();

export function getConnector(keyOrAlias: string): ConnectorDefinition | undefined {
  if (!keyOrAlias) return undefined;
  return LOOKUP.get(keyOrAlias.toLowerCase());
}

/**
 * Resolve any picker id, display name, or stored connectorKey to the
 * canonical DataStore connectorKey. Falls back to the input if unknown
 * (preserves forward compatibility).
 */
export function resolveConnectorKey(input: string): string {
  const found = getConnector(input);
  return found?.connectorKey ?? input;
}

/**
 * Key used by GenericConnectorForm to load the JSON template.
 * Preserves legacy special cases (e.g. microsoftTeams → ms-team).
 */
export function resolveTemplateId(input: string): string {
  const found = getConnector(input);
  if (!found) return input.toLowerCase();
  return (found.templateId || found.connectorKey).toLowerCase();
}

export function listByCategory(
  categoryId?: ConnectionCategoryId | null,
  options?: { status?: ConnectorStatus | 'all'; includeComingSoon?: boolean }
): ConnectorDefinition[] {
  const statusFilter = options?.status ?? 'all';
  const includeComingSoon = options?.includeComingSoon ?? true;

  return CONNECTORS.filter((c) => {
    if (categoryId && c.category !== categoryId) return false;
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (!includeComingSoon && c.status === 'coming_soon') return false;
    return true;
  });
}

export function getCategoryLabel(categoryId: string): string {
  return CATEGORY_BY_ID[categoryId as ConnectionCategoryId]?.label ?? categoryId;
}

export function getConnectorDisplayName(connectorKey: string): string {
  return getConnector(connectorKey)?.displayName ?? connectorKey;
}

export function filterConnectorsByCapability(
  capability: ConnectorCapability
): ConnectorDefinition[] {
  return CONNECTORS.filter(
    (c) => c.status === 'available' && c.capabilities.includes(capability)
  );
}

export function searchConnectors(
  query: string,
  options?: { categoryId?: ConnectionCategoryId | null; includeComingSoon?: boolean }
): ConnectorDefinition[] {
  const q = query.trim().toLowerCase();
  return listByCategory(options?.categoryId, {
    includeComingSoon: options?.includeComingSoon ?? true,
  }).filter((c) => {
    if (!q) return true;
    return (
      c.displayName.toLowerCase().includes(q) ||
      c.description.toLowerCase().includes(q) ||
      c.connectorKey.toLowerCase().includes(q) ||
      (c.aliases || []).some((a) => a.toLowerCase().includes(q))
    );
  });
}
